import { createContext, useContext, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Gamepad2, Menu, X, FileSignature, MapPin, Clock, Phone } from "lucide-react";
import { WaiverModal } from "@/components/WaiverModal";
import { CheckoutButton } from "@/components/CheckoutButton";
import { useContent, parseList } from "@/lib/content";

const WaiverContext = createContext(() => {});
export const useWaiver = () => useContext(WaiverContext);

const NAV = [
  { to: "/lounge", label: "Lounge & Soda Bar", testId: "nav-lounge-link" },
  { to: "/repair", label: "Repair Hub", testId: "nav-repair-link" },
  { to: "/maker", label: "Maker Studio", testId: "nav-maker-link" },
  { to: "/business-it", label: "Business IT & Cyber", testId: "nav-business-it-link" },
  { to: "/events", label: "Events & Bookings", testId: "nav-events-link" },
  { to: "/ai-studio", label: "AI Game Lab", testId: "nav-ai-studio-link" },
];

export default function Layout() {
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const content = useContent();
  const hours = parseList(content["settings.hours"]);

  const linkCls = ({ isActive }) =>
    `text-sm font-medium transition-colors hover:text-neon-cyan ${isActive ? "text-neon-cyan" : "text-slate-300"}`;

  return (
    <WaiverContext.Provider value={() => setWaiverOpen(true)}>
      <div className="min-h-screen bg-ink text-slate-200">
        <header className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/80 backdrop-blur-xl">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
            <Link to="/" data-testid="nav-home-logo" className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-neon-cyan/10 text-neon-cyan ring-1 ring-neon-cyan/40">
                <Gamepad2 className="h-5 w-5" />
              </span>
              <span className="font-display text-lg font-black uppercase leading-none tracking-tight text-white">
                The Game Room
                <span className="block font-mono text-[10px] font-normal tracking-[0.3em] text-neon-cyan">PUTNAM · CT</span>
              </span>
            </Link>

            <nav className="hidden items-center gap-6 lg:flex">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} data-testid={n.testId} className={linkCls}>{n.label}</NavLink>
              ))}
            </nav>

            <div className="hidden items-center gap-3 lg:flex">
              <button data-testid="sign-waiver-header-button" onClick={() => setWaiverOpen(true)}
                className="inline-flex items-center gap-2 rounded-md border border-neon-gold/50 px-4 py-2 font-display text-xs font-bold uppercase tracking-wide text-neon-gold transition-colors hover:bg-neon-gold hover:text-ink">
                <FileSignature className="h-4 w-4" /> Sign Waiver
              </button>
              <CheckoutButton lookupKey="day_pass" testId="header-day-pass-button"
                className="rounded-md bg-neon-cyan px-4 py-2 font-display text-xs font-extrabold uppercase tracking-wide text-ink transition-colors hover:bg-white">
                Day Pass · $25
              </CheckoutButton>
            </div>

            <button data-testid="mobile-menu-button" className="text-slate-200 lg:hidden" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {menuOpen && (
            <div className="border-t border-white/10 bg-slate-950/95 px-4 py-4 lg:hidden" data-testid="mobile-menu">
              <div className="flex flex-col gap-4">
                {NAV.map((n) => (
                  <NavLink key={n.to} to={n.to} data-testid={`${n.testId}-mobile`} className={linkCls}
                    onClick={() => setMenuOpen(false)}>{n.label}</NavLink>
                ))}
                <button data-testid="sign-waiver-mobile-button"
                  onClick={() => { setMenuOpen(false); setWaiverOpen(true); }}
                  className="inline-flex items-center gap-2 text-left font-display text-sm font-bold uppercase text-neon-gold">
                  <FileSignature className="h-4 w-4" /> Sign Waiver
                </button>
              </div>
            </div>
          )}
        </header>

        <main>
          <Outlet />
        </main>

        <footer className="border-t border-white/10 bg-inkalt/60">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-3">
            <div>
              <div className="mb-3 flex items-center gap-2">
                <Gamepad2 className="h-5 w-5 text-neon-cyan" />
                <span className="font-display text-base font-black uppercase text-white">The Game Room</span>
              </div>
              <p className="text-sm text-slate-400">
                {content["footer.tagline"]}
              </p>
            </div>
            <div className="text-sm text-slate-400">
              <p className="mb-3 font-display text-xs font-bold uppercase tracking-widest text-neon-cyan">Visit</p>
              <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-neon-magenta" /> {content["settings.address1"]}, {content["settings.address2"]}</p>
              <p className="mt-2 flex items-center gap-2"><Phone className="h-4 w-4 text-neon-magenta" /> {content["settings.phone"]}</p>
              <div className="mt-3 flex items-start gap-2">
                <Clock className="mt-0.5 h-4 w-4 text-neon-magenta" />
                <div>
                  {hours.map((h) => (
                    <p key={h[0]} className="flex justify-between gap-6 font-mono text-xs"><span>{h[0]}</span><span className="text-slate-300">{h[1]}</span></p>
                  ))}
                </div>
              </div>
            </div>
            <div className="text-sm">
              <p className="mb-3 font-display text-xs font-bold uppercase tracking-widest text-neon-cyan">Quick Links</p>
              <div className="grid grid-cols-2 gap-2">
                {NAV.map((n) => (
                  <Link key={n.to} to={n.to} data-testid={`footer-${n.testId}`} className="text-slate-400 transition-colors hover:text-neon-cyan">{n.label}</Link>
                ))}
                <button data-testid="footer-sign-waiver-button" onClick={() => setWaiverOpen(true)} className="text-left text-neon-gold hover:text-white">Sign Waiver</button>
                <Link to="/admin" data-testid="footer-admin-link" className="text-slate-500 transition-colors hover:text-slate-300">Admin Portal</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-white/5 py-4 text-center font-mono text-[11px] tracking-widest text-slate-600">
            © {new Date().getFullYear()} THE GAME ROOM · PUTNAM, CT · INSERT COIN TO CONTINUE
          </div>
        </footer>

        <WaiverModal open={waiverOpen} onOpenChange={setWaiverOpen} />
      </div>
    </WaiverContext.Provider>
  );
}
